import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getSliders, deleteSlider } from "../../redux/apiCalls";
import { DeleteOutline } from "@material-ui/icons";
import Table from "../../components/advancedTable";

export default function SliderList() {
    const dispatch = useDispatch();
    const products = useSelector((state) => state.product.sliders);
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);

    const handleDelete = (id) => {
        deleteSlider(id, dispatch);
        setDeleteConfirmId(null);
    };

    useEffect(() => {
        getSliders(dispatch);
    }, [dispatch]);



    const columns = [
        {
            accessor: "url",
            Header: "Slider URL",
            sortable: false,
            Cell: ({ value }) => (
                <div className="initialPadding flex justify-center">
                    {value ? (
                        <a 
                            href={value} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 font-medium hover:underline transition-colors duration-200"
                        >
                            {value.length > 50 ? `${value.substring(0, 50)}...` : value}
                        </a>
                    ) : (
                        <span className="text-slate-400 italic">No URL</span>
                    )}
                </div>
            ),
        },
        {
            accessor: "img",
            Header: "Desktop Image",
            sortable: false,
            Cell: (params) => (
                <div className="initialPadding flex justify-center">
                    {params.row.original.img ? (
                        <div 
                            className="relative group cursor-pointer"
                            onClick={() => window.open(params.row.original.img, '_blank')}
                        >
                            <img
                                src={params.row.original.img}
                                alt="Desktop Slider"
                                className="w-24 h-16 object-cover rounded-lg shadow-lg border-2 border-slate-200 hover:border-blue-500 transition-all duration-300 transform group-hover:scale-110"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 rounded-lg transition-all duration-300 flex items-center justify-center">
                                <span className="text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                    🔍 View
                                </span>
                            </div>
                        </div>
                    ) : (
                        <div className="w-24 h-16 bg-slate-100 rounded-lg flex items-center justify-center border-2 border-dashed border-slate-300">
                            <span className="text-slate-400 text-xs">No Image</span>
                        </div>
                    )}
                </div>
            ),
        },
        {
            accessor: "mobileImg",
            Header: "Mobile Image",
            sortable: false,
            Cell: (params) => (
                <div className="initialPadding flex justify-center">
                    {params.row.original.mobileImg ? (
                        <div 
                            className="relative group cursor-pointer"
                            onClick={() => window.open(params.row.original.mobileImg, '_blank')}
                        >
                            <img
                                src={params.row.original.mobileImg}
                                alt="Mobile Slider"
                                className="w-16 h-24 object-cover rounded-lg shadow-lg border-2 border-slate-200 hover:border-purple-500 transition-all duration-300 transform group-hover:scale-110"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 rounded-lg transition-all duration-300 flex items-center justify-center">
                                <span className="text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                    🔍 View
                                </span>
                            </div>
                        </div>
                    ) : (
                        <div className="w-16 h-24 bg-slate-100 rounded-lg flex items-center justify-center border-2 border-dashed border-slate-300">
                            <span className="text-slate-400 text-xs">No Image</span>
                        </div>
                    )}
                </div>
            ),
        },
        {
            accessor: "actions",
            Header: "Actions",
            sortable: false,
            Cell: (params) => (
                <div className="initialPadding flex justify-center">
                    <button
                        onClick={() => setDeleteConfirmId(params.row.original._id)}
                        className="px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-medium rounded-lg shadow-lg transition-all duration-300 transform hover:scale-105 flex items-center gap-2"
                    >
                        <DeleteOutline className="text-sm" />
                        <span>Delete</span>
                    </button>
                </div>
            ),
        },
    ];

    return (
        <div className="w-full bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
            <div className="productList bg-white rounded-3xl shadow-2xl p-8 m-6 border border-slate-200/50">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-slate-800 mb-2">Slider Management</h1>
                    <p className="text-slate-600">Manage website banner sliders for desktop and mobile displays</p>
                </div>

                {/* Statistics Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 rounded-2xl p-6 shadow-2xl border border-blue-500/30 relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-blue-400/20 to-transparent rounded-full -mr-12 -mt-12"></div>
                        <div className="relative z-10">
                            <div className="flex items-center justify-between mb-4">
                                <span className="text-blue-100 text-lg font-semibold tracking-wide">Total Sliders</span>
                                <div className="w-10 h-10 bg-blue-500/30 rounded-lg flex items-center justify-center">
                                    <span className="text-lg">🖼️</span>
                                </div>
                            </div>
                            <div className="text-3xl font-bold text-white mb-2">
                                {products ? products.length : 0}
                            </div>
                            <div className="text-blue-200 text-sm">Active Banner Slides</div>
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-green-600 via-green-700 to-green-800 rounded-2xl p-6 shadow-2xl border border-green-500/30 relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-green-400/20 to-transparent rounded-full -mr-12 -mt-12"></div>
                        <div className="relative z-10">
                            <div className="flex items-center justify-between mb-4">
                                <span className="text-green-100 text-lg font-semibold tracking-wide">Desktop Images</span>
                                <div className="w-10 h-10 bg-green-500/30 rounded-lg flex items-center justify-center">
                                    <span className="text-lg">🖥️</span>
                                </div>
                            </div>
                            <div className="text-3xl font-bold text-white mb-2">
                                {products ? products.filter(p => p.img).length : 0}
                            </div>
                            <div className="text-green-200 text-sm">Desktop Banners</div>
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-purple-600 via-purple-700 to-purple-800 rounded-2xl p-6 shadow-2xl border border-purple-500/30 relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-purple-400/20 to-transparent rounded-full -mr-12 -mt-12"></div>
                        <div className="relative z-10">
                            <div className="flex items-center justify-between mb-4">
                                <span className="text-purple-100 text-lg font-semibold tracking-wide">Mobile Images</span>
                                <div className="w-10 h-10 bg-purple-500/30 rounded-lg flex items-center justify-center">
                                    <span className="text-lg">📱</span>
                                </div>
                            </div>
                            <div className="text-3xl font-bold text-white mb-2">
                                {products ? products.filter(p => p.mobileImg).length : 0}
                            </div>
                            <div className="text-purple-200 text-sm">Mobile Banners</div>
                        </div>
                    </div>
                </div>

                {/* Table */}
                <Table
                    data={products || []}
                    columns={columns}
                    pageCount={Math.ceil((products?.length || 0) / 16)}
                    totalCount={products?.length || 0}
                    updatePagination={() => {}}
                    paginationState={{ page: 1, limit: 16 }}
                    updateLoaded={() => {}}
                    hidePagination={(products?.length || 0) <= 16}
                />
            </div>

            {/* Delete Confirmation Modal */}
            {deleteConfirmId && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 border border-slate-200">
                        <div className="text-center">
                            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                <DeleteOutline className="text-red-600 text-2xl" />
                            </div>
                            <h3 className="text-2xl font-bold text-slate-800 mb-4">Delete Slider</h3>
                            <p className="text-slate-600 mb-8">
                                Are you sure you want to delete this slider? This action cannot be undone.
                            </p>
                            <div className="flex gap-4 justify-center">
                                <button
                                    onClick={() => setDeleteConfirmId(null)}
                                    className="px-6 py-3 bg-slate-200 hover:bg-slate-300 text-slate-700 font-medium rounded-xl transition-all duration-300"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => handleDelete(deleteConfirmId)}
                                    className="px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-medium rounded-xl shadow-lg transition-all duration-300 transform hover:scale-105"
                                >
                                    Delete Slider
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
